import styled, { SimpleInterpolation, css } from 'styled-components';
import { COLOURS, SPACES, mediaMap } from '@belong/themes';
import { CONTROL_MARGIN_BOTTOM, OFFSET_CONTROL_AMOUNT, SLIDE_GUTTERS, TControlAction } from './Carousel.types';

export const Container = styled.div`
  position: relative;
  overflow: clip;
  @supports not (overflow: clip) {
    overflow: hidden;
  }
`;

export const Slides = styled.ul<{ cssAnimation: { transform: string; transition: string } }>`
  display: flex;
  flex-wrap: nowrap;
  /* CSS controls below here */
  touch-action: pan-y pinch-zoom;
  ${({ cssAnimation }) => {
    const { transform, transition } = cssAnimation;
    return css`
      transform: translateX(${transform});
      @media not (prefers-reduced-motion: reduce) {
        transition: ${transition};
      }
    `;
  }}
`;

export const Slide = styled.li<{
  totalColumns: number;
  visibleColumns: number;
  cssAnimation: { opacity: number; transition: { duration: number; ease: string } };
}>`
  list-style: none;
  ${({ totalColumns, visibleColumns }) => css`
    /*
    this width property is required to correct a safari bug in iPhone 13 where the
    flex-basis is not respected as the component re-renders. The width can be set 
    to anything but as a fail safe it's set to the same width as the flex-basis.
    */
    width: ${(visibleColumns / totalColumns) * 100}%;
    flex: 0 0 ${(visibleColumns / totalColumns) * 100}%;
  `}

  /* CSS controls below here */
  ${({ cssAnimation }) => {
    const { opacity } = cssAnimation;
    const { duration, ease } = cssAnimation.transition;
    return css`
      opacity: ${opacity};
      @media not (prefers-reduced-motion: reduce) {
        transition: opacity ${duration}s ${ease};
      }
    `;
  }}

  ${mediaMap(SLIDE_GUTTERS, (padding: number) => {
    return css`
      padding: 0 ${padding / 2}rem;
    `;
  })}

  /* reset the slide layout */
  && > * {
    max-width: unset;
    min-width: unset;
    margin-left: unset;
    margin-right: unset;
    padding-left: unset;
    padding-right: unset;
  }
`;

export const Controls = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const Control = styled.button<{ controlAction: TControlAction }>`
  background-color: ${COLOURS.GREY_600};
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.6rem;
  outline: none;
  width: 4rem;
  height: 4rem;
  opacity: 0.75;
  position: sticky;
  z-index: 1;
  top: calc(50vh - 2rem);
  margin-top: ${SPACES.xxxlarge}rem;
  pointer-events: visible;
  padding: auto 0;

  ${mediaMap(CONTROL_MARGIN_BOTTOM, (marginBottom: string) => {
    return css`
      margin-bottom: ${marginBottom};
    `;
  })}

  ${({ controlAction }): SimpleInterpolation =>
    controlAction === 'increase'
      ? mediaMap(
          OFFSET_CONTROL_AMOUNT,
          (offset: number) => css`
            margin-left: auto;
            margin-right: ${offset}rem;
          `
        )
      : mediaMap(
          OFFSET_CONTROL_AMOUNT,
          (offset: number) => css`
            margin-left: ${offset}rem;
          `
        )}

  &:hover {
    opacity: 1;
  }
`;

export const SteppedProgressBar = styled.div`
  ${mediaMap(SLIDE_GUTTERS, (padding: number) => {
    return css`
      padding: 2rem ${padding / 2}rem;
    `;
  })}
`;
